import GOOGLE_MAP_STYLES from './wp-map-styles';
import { loadAPI } from '@/api/google-maps';

const io = new IntersectionObserver(onIntersection, {
  rootMargin: '50px 0px',
  threshold: 0.01,
});

function onIntersection(entries) {
  entries.forEach((entry) => {
    if (!entry.intersectionRatio) {
      return;
    }

    if ('onIntersection' in entry.target) {
      entry.target.onIntersection();
    }
  });
}

class WPMap extends HTMLElement {
  /**
   * Tag name of element
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-map';
  }

  /**
   * Getter for `loaded` attribute
   */
  get loaded() {
    return this.hasAttribute('loaded');
  }

  /**
   * Setter for `loaded` attribute
   */
  set loaded(value) {
    if (Boolean(value)) {
      this.setAttribute('loaded', '');
    } else {
      this.removeAttribute('loaded');
    }
  }

  /**
   * Getter for `unobserve` attribute
   */
  get unobserve() {
    return this.hasAttribute('unobserve');
  }

  /**
   * Setter for `unobserve` attribute
   */
  set unobserve(value) {
    if (Boolean(value)) {
      this.setAttribute('unobserve', '');
    } else {
      this.removeAttribute('unobserve');
    }
  }

  /**
   * Getter for `enable-scroll` attribute
   */
  get enableScroll() {
    return this.hasAttribute('enable-scroll');
  }

  /**
   * Setter for `enable-scroll` attribute
   */
  set enableScroll(value) {
    if (Boolean(value)) {
      this.setAttribute('enable-scroll', '');
    } else {
      this.removeAttribute('enable-scroll');
    }
  }

  /**
   * Getter for `latitude` attribute
   */
  get latitude() {
    return this.hasAttribute('latitude')
      ? parseFloat(this.getAttribute('latitude'))
      : 0;
  }

  /**
   * Setter for `latitude` attribute
   */
  set latitude(value) {
    this.setAttribute('latitude', parseFloat(value));
  }

  /**
   * Getter for `longitude` attribute
   */
  get longitude() {
    return this.hasAttribute('longitude')
      ? parseFloat(this.getAttribute('longitude'))
      : 0;
  }

  /**
   * Setter for `longitude` attribute
   */
  set longitude(value) {
    this.setAttribute('longitude', parseFloat(value));
  }

  /**
   * Getter for `options` attribute
   */
  get options() {
    return this.hasAttribute('options')
      ? Object.assign(
          {},
          this._options,
          JSON.parse(this.getAttribute('options')),
        )
      : this._options;
  }

  /**
   * Setter for `options` attribute
   */
  set options(value) {
    this._options = Object.assign({}, this._options, value);

    if (this.map) {
      this.map.setOptions(this._options);
    }
  }

  /**
   * Getter for `api-key` attribute
   */
  get apiKey() {
    return this.hasAttribute('api-key') ? this.getAttribute('api-key') : '';
  }

  /**
   * Setter for `api-key` attribute
   */
  set apiKey(value) {
    if (value) {
      this.setAttribute('api-key', value);
    } else {
      this.removeAttribute('api-key');
    }
  }

  /**
   * Computed property for markers
   * @returns {Array} Array of marker HTML elements
   */
  get _markers() {
    return [...this.querySelectorAll('-marker')];
  }

  /**
   * Creates an instance of WPMap.
   * @param {HTMLElement} self Element
   * @constructor
   */
  constructor(self) {
    self = super(self);
    this.map = null;
    this._options = {};
    this.info = null;

    return self;
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    if (!this.unobserve) {
      io.observe(this);
    }
  }

  /**
   * Lifecycle event when element is detach from DOM
   */
  disconnectedCallback() {
    // TODO - Remove default `resize` listener from Google Maps API
    io.unobserve(this);
  }

  /**
   * Add event listeners to element
   */
  _addEventListeners() {
    this.addEventListener('mapchanged', this._mapChanged, false);
    this.addEventListener('infowindowopen', this._infoWindowOpen, false);
    this.addEventListener('infowindowclosed', this._infoWindowClosed, false);
  }

  onIntersection() {
    this.load();
    io.unobserve(this);
  }

  async load() {
    // Load Google Maps API
    await loadAPI(this.apiKey);
    this._initMap();
    this._addEventListeners();
    google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      this.dispatchEvent(
        new CustomEvent('mapready', {
          detail: this,
        }),
      );
      this.loaded = true;
    });
  }

  /**
   * Handler for `mapchanged` event listener
   */
  _mapChanged() {
    this.center();
  }

  /**
   * Handler for `infowindowopen` event listener
   */
  _infoWindowOpen(evt) {
    const markerEl = evt.target;

    // If infoWindow object doesn't exist, create one
    if (!this.info) {
      this.info = new google.maps.InfoWindow();
    }

    // Update content
    this.info.setContent(markerEl.innerHTML);
    this.info.open(this.map, markerEl.marker);

    // Add listener for close
    google.maps.event.addListener(this.info, 'closeclick', function () {
      markerEl.open = false;
    });
  }

  /**
   * Handler for `infowindowclosed` event listener
   * @callback
   */
  _infoWindowClosed() {
    if (this.info) {
      this.info.close();
    }
  }

  /**
   * Initalise the map using the Google Maps API
   * @private
   */
  _initMap() {
    // Default options
    this._options = {
      scrollwheel: false,
      zoom: 16,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      center: {
        lat: this.latitude,
        lng: this.longitude,
      },
      styles: GOOGLE_MAP_STYLES,
    };

    // Create element for map
    const map = document.createElement('div');

    map.style.display = 'block';
    map.style.width = '100%';
    map.style.height = '100%';
    map.style.position = 'absolute';
    this.appendChild(map);

    // Create map
    this.map = new google.maps.Map(map, this.options);

    // Add markers array
    this.map.markers = [];

    // Center map
    google.maps.event.addListenerOnce(
      this.map,
      'idle',
      function () {
        this.center();
      }.bind(this),
    );

    // On click, activate scrollwheel
    google.maps.event.addListener(
      this.map,
      'click',
      function () {
        if (this.enableScroll) {
          this.map.setOptions({
            scrollwheel: true,
          });
        }

        // Close info window if one exists
        if (this.info) {
          this._markers.forEach((marker) => {
            marker.open = false;
          });
        }
      }.bind(this),
    );
  }

  center() {
    if (!this.map) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();

    // loop through all markers and create bounds
    this._markers.forEach((marker) => {
      const latlng = new google.maps.LatLng(marker.latitude, marker.longitude);

      bounds.extend(latlng);
    });

    // only 1 marker?
    if (this._markers.length === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(16);
      this.map.panBy(0, -20);
    } else {
      this.map.fitBounds(bounds);
    }
  }

  panTo(lat, lng) {
    if (!this.map) {
      return;
    }

    const center = new google.maps.LatLng(lat, lng);

    this.map.panTo(center);
  }

  panBy(x = 0, y = 0) {
    if (!this.map) {
      return;
    }

    this.map.panBy(x, y);
  }

  setBounds(radius = 0, lat, lng) {
    const circleOptions = {
      center: new google.maps.LatLng(lat, lng),
      fillOpacity: 0,
      strokeOpacity: 0,
      map: this._map,
      radius: radius,
    };
    const myCircle = new google.maps.Circle(circleOptions);
    this.map.fitBounds(myCircle.getBounds());
  }

  get markerIsVisible() {
    let bool = false;

    this._markers.forEach((marker) => {
      if (this.map.getBounds().contains(marker.marker.getPosition())) {
        bool = true;
      }
    });

    return bool;
  }
}

customElements.define(WPMap.is, WPMap);
